import { Component, Input, OnInit, OnDestroy, Inject } from "@angular/core";
import { FormGroup, FormControl, FormArray, FormBuilder, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { asEnumerable } from "linq-es2015";
import { CeRequestOptions } from "../../request-header/request-header.component";
import { GeNameValidator } from "./validators/ge-name.validator";
import { CE_REQUEST_OPTIONS, ResolvedRequestOptionsService } from "../../services/resolved-request-options";
import { ExperimentDesignFormValidatorsService } from "../experiment-design-form/experiment-design-form-validators.service";
import { Crop, CropService } from "../../../shared/services/crop.service";
import { PollinationTypeEnum } from "../../../shared/services/pollination-stake-label.service";
import { PollinationCategory, PollinationCategoryEnum } from "../../../shared/services/pollination-category.service";
import { ConjunctionEnum } from "../../../shared/models/conjunction.enum";

@Component({
  selector: "app-pollination-instructions",
  templateUrl: "./pollination-instructions.component.html",
  styleUrls: ["./pollination-instructions.component.css"],
  providers: [{ provide: CE_REQUEST_OPTIONS, useClass: ResolvedRequestOptionsService }]
})
export class PollinationInstructionsComponent implements OnInit, OnDestroy {
  @Input() experimentDesignFormGroup: FormGroup;
  @Input() crop: number;

  public ceRequestOptions: CeRequestOptions;
  public pollinationCategoryOptions: PollinationCategory[];
  public focusedPreference: number = -1;
  public showDonorNeeded: boolean = false;
  public tooltipMessage: string = "";
  public pollinationCategoryTooltipText: string = "";
  public initialTooltipMessage: string = "This toggle notifies your Tactical Liaison to submit a corresponding MyCE request to plant pollen or silk donors";
  public crossPollinationTooltipMessage: string = "When selecting a cross pollination option (carry pollen out or carry pollen in), donors will likely be needed";
  public showPollinationInstructions: boolean = true;
  public canAddPreferenceGroup: boolean = false;
  public canDeletePreferenceGroup: boolean = false;
  private pollinationCategorySubscription: Subscription;

  public get pollinationInstructions(): FormGroup {
    return <FormGroup>this.experimentDesignFormGroup.get("pollinationInstructions");
  }

  public get pollinationPreferences() {
    return <FormArray>this.experimentDesignFormGroup.get("pollinationInstructions.pollinationPreferences");
  }

  public get pollinationCategory() {
    return <FormControl>this.pollinationInstructions.get("pollinationCategory");
  }

  public get donorsNeeded(): FormControl {
    return <FormControl>this.pollinationInstructions.get('donorsNeeded');
  }

  constructor(
    private formBuilder: FormBuilder,
    private geNameValidator: GeNameValidator,
    private formValidators: ExperimentDesignFormValidatorsService,
    private cropService: CropService,
    @Inject(CE_REQUEST_OPTIONS) private routeData: ResolvedRequestOptionsService
  ) {
    this.ceRequestOptions = this.routeData.requestOptions;
  }

  public ngOnInit() {
    this.cropService.get(this.crop).subscribe(
      (x: Crop) => {
        this.showDonorNeeded = x.isDonorNeeded;
      }
    );

    this.pollinationCategoryOptions = asEnumerable(this.ceRequestOptions.pollinationCategories)
      .Where(s => s.isActive)
      .OrderBy(s => s.order)
      .ToArray();

    this.tooltipMessage = this.initialTooltipMessage;
    this.pollinationCategoryTooltipText = 'This field specifies what category of pollinations will be performed, and the options available for those categories.<br><br>' +
      'Standard: This covers most experiments. Two pollination options are allowed & required - "None" should be used as the secondary option if back-up instruction is not desired.<br><br>' +
      'Zygosity Dependent: Allows pollination instructions to be specified based on zygosity. Each zygosity follows the same rules as the "Standard" category.<br><br>' +
      'Crossing block: Use for crossing block experiments only for situations approved by your pipeline and TL.';
    this.setupPollinationCategoryListener();

    const currentCategoryValue = this.pollinationCategory ? this.pollinationCategory.value : null;
    this.updatePollinationInstructionsState(currentCategoryValue);
  }

  public ngOnDestroy() {
    if (this.pollinationCategorySubscription) {
      this.pollinationCategorySubscription.unsubscribe();
    }
  }

  private setupPollinationCategoryListener(): void {
    if (this.pollinationCategory && this.pollinationCategory.valueChanges) {
      this.pollinationCategorySubscription = this.pollinationCategory.valueChanges
        .pipe(debounceTime(50))
        .subscribe(categoryId => {
          this.updatePollinationInstructionsState(categoryId);
        });
    }
  }

  private updatePollinationInstructionsState(categoryId: number): void {
    const selectedCategory = this.pollinationCategoryOptions.find(option => option.id === categoryId);
    
    if (!categoryId || !selectedCategory) {
      this.showPollinationInstructions = false;
      this.canAddPreferenceGroup = false;
      this.canDeletePreferenceGroup = false;
      this.adjustPollinationPreferenceGroupCount(0);
      this.pollinationPreferences.clearValidators();
      this.pollinationPreferences.updateValueAndValidity();
      if (this.showDonorNeeded) {
        this.pollinationInstructions.get('donorsNeeded').setValue(false);
        this.tooltipMessage = this.initialTooltipMessage;
      }
      return;
    }

    const numberOfGroups = selectedCategory.numberOfPreferenceGroups;

    this.showPollinationInstructions = selectedCategory.id !== PollinationCategoryEnum.CrossingBlock;
    this.canAddPreferenceGroup = false;
    this.canDeletePreferenceGroup = false;

    if (selectedCategory.id === PollinationCategoryEnum.CrossingBlock) {
      this.pollinationPreferences.clearValidators();
    } else {
      this.pollinationPreferences.setValidators(Validators.required);
    }

    this.adjustPollinationPreferenceGroupCount(numberOfGroups);

    if (numberOfGroups > 0) {
      this.pollinationInstructions.updateValueAndValidity();
    }
    
    this.pollinationPreferences.updateValueAndValidity();
    
    this.checkAndResetDonorsNeeded();
  }

  private adjustPollinationPreferenceGroupCount(targetCount: number): void {
    const currentCount = this.pollinationPreferences.length;

    if (currentCount === targetCount) {
      if (targetCount > 0 && this.focusedPreference === -1) {
        this.focusedPreference = 0;
      }
      return;
    }

    while (this.pollinationPreferences.length > 0) {
      this.pollinationPreferences.removeAt(0);
    }

    for (let i = 0; i < targetCount; i++) {
      this.addPreferenceGroup();
    }

    if (targetCount > 0) {
      this.focusedPreference = 0;
      setTimeout(() => {
        this.pollinationPreferences.controls.forEach(control => {
          control.markAsTouched();
          control.updateValueAndValidity();
        });
      }, 0);
    } else {
      this.focusedPreference = -1;
    }
  }

  public canAddPollinationInstruction(preferenceGroupIndex: number, pollinationTypeId?: number): boolean {
    if (!this.pollinationCategory.value) {
      return false;
    }

    const selectedCategory = this.pollinationCategoryOptions.find(option => option.id === this.pollinationCategory.value);
    if (!selectedCategory) {
      return false;
    }

    const instructionsPerPair = selectedCategory.numberOfInstructionsPerPair;

    if (selectedCategory.id === PollinationCategoryEnum.CrossingBlock) {
      return false;
    }

    const nonePollinationType = this.ceRequestOptions.pollinationTypes.find(pt => pt.id === PollinationTypeEnum.None);
    const isAddingNone = pollinationTypeId && nonePollinationType && pollinationTypeId === nonePollinationType.id;

    if (this.groupHasNone(preferenceGroupIndex)) {
      return false;
    }

    if (isAddingNone) {
      const currentGroupNonNoneInstructions = this.getNonNoneInstructionsInGroup(preferenceGroupIndex);
      if (currentGroupNonNoneInstructions > 0) {
        return false;
      }
      return true;
    }

    if (selectedCategory.id === PollinationCategoryEnum.Standard) {
      const totalNonNoneInstructions = this.getTotalNonNoneInstructions();
      const currentGroupNonNoneInstructions = this.getNonNoneInstructionsInGroup(preferenceGroupIndex);

      return totalNonNoneInstructions < instructionsPerPair && currentGroupNonNoneInstructions < instructionsPerPair;
    }

    if (selectedCategory.id === PollinationCategoryEnum.ZygosityDependent) {
      const pairIndex = Math.floor(preferenceGroupIndex / 2);
      const pairStartIndex = pairIndex * 2;
      const pairEndIndex = pairStartIndex + 1;

      const pairTotalInstructions = this.getNonNoneInstructionsInGroup(pairStartIndex) +
        this.getNonNoneInstructionsInGroup(pairEndIndex);

      const currentGroupZygosityInstructions = this.getNonNoneInstructionsInGroup(preferenceGroupIndex);

      return pairTotalInstructions < instructionsPerPair && currentGroupZygosityInstructions < instructionsPerPair;
    }

    return false;
  }

  private groupHasNone(groupIndex: number): boolean {
    const nonePollinationType = this.ceRequestOptions.pollinationTypes.find(pt => pt.id === PollinationTypeEnum.None);
    if (!nonePollinationType) return false;

    const group = this.pollinationPreferences.at(groupIndex) as FormGroup;
    if (!group) return false;

    const items = group.get('items') as FormArray;
    if (!items) return false;

    for (let i = 0; i < items.length; i++) {
      const item = items.at(i) as FormGroup;
      const pollinationTypeControl = item.get('pollinationType');
      if (pollinationTypeControl && pollinationTypeControl.value === nonePollinationType.id) {
        return true;
      }
    }
    return false;
  }

  private getTotalNonNoneInstructions(): number {
    let total = 0;
    for (let i = 0; i < this.pollinationPreferences.length; i++) {
      total += this.getNonNoneInstructionsInGroup(i);
    }
    return total;
  }

  private getNonNoneInstructionsInGroup(groupIndex: number): number {
    const group = this.pollinationPreferences.at(groupIndex) as FormGroup;
    if (!group) return 0;

    const items = group.get('items') as FormArray;
    if (!items) return 0;

    let count = 0;
    for (let i = 0; i < items.length; i++) {
      const item = items.at(i) as FormGroup;
      const pollinationTypeControl = item.get('pollinationType');
      const pollinationType = pollinationTypeControl ? pollinationTypeControl.value : null;

      if (pollinationType !== null && pollinationType !== undefined) {
        const pollinationTypeObj = this.ceRequestOptions.pollinationTypes.find(pt => pt.id === pollinationType);
        if (pollinationTypeObj && pollinationTypeObj.id !== PollinationTypeEnum.None) {
          count++;
        }
      }
    }
    return count;
  }

  public addPreferenceGroup(): void {
    const newPreference = this.formBuilder.group({
      order: this.pollinationPreferences.length + 1,
      conjunction: this.formBuilder.control({ value: ConjunctionEnum.And, disabled: false }, []),
      items: this.formBuilder.array([]),
    }, { validator: this.formValidators.pollinationPreferenceGroupValidator() });

    this.pollinationPreferences.push(newPreference);

    this.focusedPreference = this.pollinationPreferences.length - 1;
  }

  public addInstruction(pollinationTypeId: number): void {
    if (this.pollinationPreferences.length === 0) {
      return;
    }

    if (!this.canAddPollinationInstruction(this.focusedPreference, pollinationTypeId)) {
      return;
    }

    const pollinationTypes = asEnumerable(this.ceRequestOptions.pollinationTypes);
    const selectedPollinationType = pollinationTypes.Single(t => t.id === pollinationTypeId);
    
    if (selectedPollinationType.id === PollinationTypeEnum.None) {
      const currentlySelectedPreference = <FormGroup>this.pollinationPreferences.at(this.focusedPreference);
      const items = <FormArray>currentlySelectedPreference.get("items");
      
      while (items.length > 0) {
        items.removeAt(0);
      }
    }

    const geName = this.formBuilder.control({ value: null, disabled: false }, []);

    if (selectedPollinationType.geNameRequired) {
      geName.setValidators([Validators.required, Validators.minLength(3)]);
      geName.setAsyncValidators(this.geNameValidator.validateGeName());
      if (this.showDonorNeeded) {
        this.pollinationInstructions.get('donorsNeeded').setValue(true);
        this.tooltipMessage = this.crossPollinationTooltipMessage;
      }
    }

    geName.patchValue(null);
    geName.updateValueAndValidity();

    const currentlySelectedPreference = <FormGroup>this.pollinationPreferences.at(this.focusedPreference);
    const items = <FormArray>currentlySelectedPreference.get("items");

    items.push(
      this.formBuilder.group({
        conjunction: this.formBuilder.control({ value: ConjunctionEnum.Or, disabled: false }, []),
        pollinationType: this.formBuilder.control({ value: pollinationTypeId, disabled: false }, []),
        geName: geName
      })
    );

    this.handleAutomaticNoneForStandard();
  }

  public deleteLastItemFromGroup(groupIndex: number): void {
    const group = this.pollinationPreferences.at(groupIndex) as FormGroup;
    if (!group) return;

    const items = group.get('items') as FormArray;
    if (!items || items.length === 0) return;

    items.removeAt(items.length - 1);

    this.handleAutomaticNoneForStandard();

    this.pollinationInstructions.updateValueAndValidity();
    
    this.checkAndResetDonorsNeeded();
  }

  private handleAutomaticNoneForStandard(): void {
    const selectedCategory = this.pollinationCategoryOptions.find(option => option.id === this.pollinationCategory.value);
    if (!selectedCategory) {
      return;
    }

    const numberOfGroups = selectedCategory.numberOfPreferenceGroups;

    if (selectedCategory.id === PollinationCategoryEnum.Standard && numberOfGroups === 2) {
      this.handleNoneForGroupPair(0, 1, selectedCategory.numberOfInstructionsPerPair);
    }
    else if (selectedCategory.id === PollinationCategoryEnum.ZygosityDependent && numberOfGroups === 6) {
      this.handleNoneForGroupPair(0, 1, selectedCategory.numberOfInstructionsPerPair);
      this.handleNoneForGroupPair(2, 3, selectedCategory.numberOfInstructionsPerPair);
      this.handleNoneForGroupPair(4, 5, selectedCategory.numberOfInstructionsPerPair);
    }
  }

  private handleNoneForGroupPair(group1Index: number, group2Index: number, instructionsPerPair: number): void {
    const group1Instructions = this.getNonNoneInstructionsInGroup(group1Index);
    const group2Instructions = this.getNonNoneInstructionsInGroup(group2Index);

    if (group1Instructions === instructionsPerPair && group2Instructions === 0) {
      this.clearGroupAndAddNone(group2Index);
    }
    else if (group2Instructions === instructionsPerPair && group1Instructions === 0) {
      this.clearGroupAndAddNone(group1Index);
    }
  }

  private clearGroupAndAddNone(groupIndex: number): void {
    const nonePollinationType = this.ceRequestOptions.pollinationTypes.find(pt => pt.id === PollinationTypeEnum.None);
    if (!nonePollinationType) return;

    const group = this.pollinationPreferences.at(groupIndex) as FormGroup;
    if (!group) return;

    const items = group.get('items') as FormArray;
    if (!items) return;

    while (items.length > 0) {
      items.removeAt(0);
    }

    const geName = this.formBuilder.control({ value: null, disabled: false }, []);
    items.push(
      this.formBuilder.group({
        conjunction: this.formBuilder.control({ value: ConjunctionEnum.Or, disabled: false }, []),
        pollinationType: this.formBuilder.control({ value: nonePollinationType.id, disabled: false }, []),
        geName: geName
      })
    );
  }

  private removeNoneFromGroup(groupIndex: number): void {
    const nonePollinationType = this.ceRequestOptions.pollinationTypes.find(pt => pt.id === PollinationTypeEnum.None);
    if (!nonePollinationType) return;

    const group = this.pollinationPreferences.at(groupIndex) as FormGroup;
    if (!group) return;

    const items = group.get('items') as FormArray;
    if (!items) return;

    for (let i = items.length - 1; i >= 0; i--) {
      const item = items.at(i) as FormGroup;
      const pollinationTypeControl = item.get('pollinationType');
      if (pollinationTypeControl && pollinationTypeControl.value === nonePollinationType.id) {
        items.removeAt(i);
      }
    }
  }

  public dropped(event) {
    let pollinationPreferencesCopy = [];
    for (let preference of this.pollinationPreferences.controls) {
      pollinationPreferencesCopy.push(preference.value);
    }
    this.pollinationPreferences.reset(pollinationPreferencesCopy);
  }

  public delete(index: number): void {
    if (!this.canDeletePreferenceGroup) {
      return;
    }

    this.pollinationPreferences.removeAt(index);
    this.checkAndResetDonorsNeeded();
    this.pollinationInstructions.updateValueAndValidity();
  }

  private checkAndResetDonorsNeeded(): void {
    if (!this.showDonorNeeded) {
      return;
    }

    const hasCrossPollination = this.hasCrossPollinationInstructions();
    
    if (!hasCrossPollination) {
      this.pollinationInstructions.get('donorsNeeded').setValue(false);
      this.tooltipMessage = this.initialTooltipMessage;
    }
  }

  private hasCrossPollinationInstructions(): boolean {
    for (let i = 0; i < this.pollinationPreferences.length; i++) {
      const group = this.pollinationPreferences.at(i) as FormGroup;
      if (!group) continue;

      const items = group.get('items') as FormArray;
      if (!items) continue;

      for (let j = 0; j < items.length; j++) {
        const item = items.at(j) as FormGroup;
        const pollinationTypeControl = item.get('pollinationType');
        
        if (pollinationTypeControl) {
          const pollinationType = pollinationTypeControl.value;
          
          if (pollinationType === PollinationTypeEnum.CarryTo || 
              pollinationType === PollinationTypeEnum.CarryFrom) {
            return true;
          }
        }
      }
    }
    
    return false;
  }

  public focus(index: number) {
    this.focusedPreference = index;
  }

  public getGroupLabel(groupIndex: number): string {
    if (!this.pollinationCategory.value) {
      return '';
    }

    const selectedCategory = this.pollinationCategoryOptions.find(option => option.id === this.pollinationCategory.value);
    if (!selectedCategory) {
      return '';
    }

    return PollinationInstructionsComponent.getGroupLabelByCategoryId(groupIndex, selectedCategory.id);
  }

  public static getGroupLabelByCategoryId(groupIndex: number, categoryId: number): string {
    switch (categoryId) {
      case PollinationCategoryEnum.Standard:
        if (groupIndex === 0) return 'Primary Standard';
        if (groupIndex === 1) return 'Secondary Standard';
        return '';

      case PollinationCategoryEnum.ZygosityDependent:
        if (groupIndex === 0) return 'Primary Homozygous';
        if (groupIndex === 1) return 'Secondary Homozygous';
        if (groupIndex === 2) return 'Primary Hemizygous';
        if (groupIndex === 3) return 'Secondary Hemizygous';
        if (groupIndex === 4) return 'Primary Null/WT';
        if (groupIndex === 5) return 'Secondary Null/WT';
        return '';
      
      default:
        return '';
    }
  }

  private static getCategoryIdFromName(categoryName: string): number | null {
    if (!categoryName) {
      return null;
    }

    switch (categoryName) {
      case 'Standard':
        return PollinationCategoryEnum.Standard;
      case 'Zygosity-dependent':
        return PollinationCategoryEnum.ZygosityDependent;
      case 'Crossing block':
        return PollinationCategoryEnum.CrossingBlock;
      default:
        return null;
    }
  }

  public static getGroupLabelForCategory(groupIndex: number, categoryName: string): string {
    const categoryId = PollinationInstructionsComponent.getCategoryIdFromName(categoryName);
    if (!categoryId) {
      return '';
    }

    return PollinationInstructionsComponent.getGroupLabelByCategoryId(groupIndex, categoryId);
  }

  public isZygosityDependent(): boolean {
    if (!this.pollinationCategory.value) {
      return false;
    }
    const selectedCategory = this.pollinationCategoryOptions.find(option => option.id === this.pollinationCategory.value);
    return selectedCategory ? selectedCategory.id === PollinationCategoryEnum.ZygosityDependent : false;
  }

  public shouldHideConjunction(groupIndex: number): boolean {
    if (groupIndex === 0) {
      return false;
    }
    
    if (!this.pollinationCategory.value) {
      return false;
    }
    
    return true;
  }

  public static shouldHideConjunctionForCategory(groupIndex: number, categoryId: number): boolean {
    if (groupIndex === 0) {
      return false;
    }
    
    if (!categoryId) {
      return false;
    }
    
    return true;
  }

  public static shouldHideConjunctionByCategoryName(groupIndex: number, categoryName: string): boolean {
    if (groupIndex === 0) {
      return false;
    }

    const categoryId = PollinationInstructionsComponent.getCategoryIdFromName(categoryName);
    if (!categoryId) {
      return false;
    }

    return PollinationInstructionsComponent.shouldHideConjunctionForCategory(groupIndex, categoryId);
  }
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-2/_work/98/s/src/app/request-creation/experiment-design/pollination-instructions/pollination-instructions.component.ts